import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { Home as HomeIcon, ClipboardList, BookOpen, Users, Wallet, User, ShieldCheck, LifeBuoy, Bell, CreditCard, Receipt, LayoutDashboard, FileCheck2, Layers, PenSquare, Tags, ListChecks, Network, Banknote, Landmark, Percent, Ticket, FileText, ScrollText, Activity, UserCog, Settings, AlertTriangle, BarChart3 } from "lucide-react";
import { LangProvider } from "./lib/i18n";
import { AuthProvider, RequireAuth } from "./lib/auth";
import { api } from "./lib/api";
import PublicLayout from "./components/PublicLayout";
import AppShell from "./components/AppShell";
import Home from "./pages/public/Home";
import { HowItWorks, Categories, PlansPage, Rewards, ReferralInfo, SecurityPage, Faq, Contact, Help, Policy } from "./pages/public/Info";
import { Login, Register, Forgot, Reset, AuthCallback } from "./pages/Auth";
import Dashboard from "./pages/partner/Dashboard";
import { Surveys, TakeSurvey } from "./pages/partner/Surveys";
import { Plans, Checkout, Receipt as ReceiptPage, Payments } from "./pages/partner/Plans";
import { Wallet as WalletPage, Withdrawals, Referrals } from "./pages/partner/Money";
import { Kyc, Support, Notifications, Profile } from "./pages/partner/Account";
import { AdminDashboard, Health, AuditLogs } from "./pages/admin/AdminCore";
import { Partners, KycQueue, Admins, RiskPrivacy } from "./pages/admin/AdminPeople";
import { SurveyList, SurveyBuilder, ResponseReview, TopicsClients, PerformanceAll } from "./pages/admin/AdminSurveys";
import { PlansAdmin, Ledgers, WithdrawalQueue, PaymentsAdmin, TaxAdmin, ReferralAdmin } from "./pages/admin/AdminFinance";
import { TicketsAdmin, Cms, SystemSettings } from "./pages/admin/AdminContent";

const P = (id, to, icon, en, hi, end) => ({ id, to, icon, en, hi, end });
const PARTNER_NAV = [
  P("home", "/app", HomeIcon, "Dashboard", "डैशबोर्ड", true), P("surveys", "/app/surveys", ClipboardList, "Surveys", "सर्वे"),
  P("ledger", "/app/ledger", BookOpen, "Ledgers", "लेजर"), P("referrals", "/app/referrals", Users, "Referrals", "रेफरल"),
  P("withdrawals", "/app/withdrawals", Wallet, "Withdraw", "निकासी"), P("plans", "/app/plans", CreditCard, "Plans", "प्लान"),
  P("payments", "/app/payments", Receipt, "Payments", "भुगतान"), P("kyc", "/app/kyc", ShieldCheck, "KYC", "KYC"),
  P("support", "/app/support", LifeBuoy, "Support", "सहायता"), P("notifications", "/app/notifications", Bell, "Alerts", "सूचनाएँ"),
  P("profile", "/app/profile", User, "Profile", "प्रोफ़ाइल"),
];
const ADMIN_NAV = [
  [P("dash", "/admin", LayoutDashboard, "Dashboard", "डैशबोर्ड", true), "dashboard"], [P("partners", "/admin/partners", Users, "Partners", "पार्टनर"), "partners"],
  [P("kyc", "/admin/kyc", FileCheck2, "KYC", "KYC"), "kyc"], [P("plans", "/admin/plans", Layers, "Plans", "प्लान"), "plans"],
  [P("surveys", "/admin/surveys", PenSquare, "Survey builder", "सर्वे बिल्डर"), "surveys"], [P("responses", "/admin/responses", ListChecks, "Responses", "उत्तर"), "responses"],
  [P("performance", "/admin/performance", BarChart3, "Performance", "प्रदर्शन"), "surveys"], [P("topics", "/admin/topics", Tags, "Topics & clients", "विषय और क्लाइंट"), "surveys"],
  [P("referrals", "/admin/referrals", Network, "Referrals", "रेफरल"), "referrals"], [P("ledgers", "/admin/ledgers", BookOpen, "Ledgers", "लेजर"), "ledgers"],
  [P("withdrawals", "/admin/withdrawals", Banknote, "Payout queue", "भुगतान कतार"), "withdrawals"], [P("payments", "/admin/payments", Landmark, "Payments", "भुगतान"), "payments"],
  [P("tax", "/admin/tax", Percent, "GST / TDS", "GST / TDS"), "tax"], [P("tickets", "/admin/tickets", Ticket, "Tickets", "टिकट"), "tickets"],
  [P("cms", "/admin/cms", FileText, "CMS & policies", "CMS और नीतियाँ"), "cms"], [P("risk", "/admin/risk", AlertTriangle, "Fraud & privacy", "धोखाधड़ी व गोपनीयता"), "partners"],
  [P("audit", "/admin/audit", ScrollText, "Audit logs", "ऑडिट लॉग"), "audit"], [P("health", "/admin/health", Activity, "Integrations", "इंटीग्रेशन"), "dashboard"],
  [P("admins", "/admin/admins", UserCog, "Admin roles", "एडमिन रोल"), "admins"], [P("settings", "/admin/settings", Settings, "Settings", "सेटिंग्स"), "settings"],
];

function AdminLayout() {
  const [perms, setPerms] = useState(null);
  useEffect(() => { api.get("/admin/me/permissions").then((r) => setPerms(r.data)); }, []);
  if (!perms) return null;
  const items = ADMIN_NAV.filter(([, m]) => perms.permissions[m]).map(([i]) => i);
  return <AppShell items={items} bottom={items.slice(0, 4)} badge={`Admin • ${perms.role.replace(/_/g, " ")}`} />;
}

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster richColors position="top-center" />
      </AuthProvider>
    </LangProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  // Google OAuth callback: session_id in URL fragment must be processed before any route/auth check
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/plans" element={<PlansPage />} />
              <Route path="/rewards" element={<Rewards />} />
              <Route path="/referral" element={<ReferralInfo />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/help" element={<Help />} />
              <Route path="/policy/:slug" element={<Policy />} />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<Forgot />} />
            <Route path="/reset-password" element={<Reset />} />
            <Route path="/app" element={<RequireAuth><AppShell items={PARTNER_NAV} bottom={PARTNER_NAV.slice(0, 4)} /></RequireAuth>}>
              <Route index element={<Dashboard />} />
              <Route path="surveys" element={<Surveys />} />
              <Route path="surveys/:id" element={<TakeSurvey />} />
              <Route path="ledger" element={<WalletPage />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="withdrawals" element={<Withdrawals />} />
              <Route path="plans" element={<Plans />} />
              <Route path="checkout/:planId" element={<Checkout />} />
              <Route path="payments" element={<Payments />} />
              <Route path="payments/:id" element={<ReceiptPage />} />
              <Route path="kyc" element={<Kyc />} />
              <Route path="support" element={<Support />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="profile" element={<Profile />} />
            </Route>
            <Route path="/admin" element={<RequireAuth admin><AdminLayout /></RequireAuth>}>
              <Route index element={<AdminDashboard />} />
              <Route path="partners" element={<Partners />} />
              <Route path="kyc" element={<KycQueue />} />
              <Route path="plans" element={<PlansAdmin />} />
              <Route path="surveys" element={<SurveyList />} />
              <Route path="surveys/:id" element={<SurveyBuilder />} />
              <Route path="responses" element={<ResponseReview />} />
              <Route path="performance" element={<PerformanceAll />} />
              <Route path="topics" element={<TopicsClients />} />
              <Route path="referrals" element={<ReferralAdmin />} />
              <Route path="ledgers" element={<Ledgers />} />
              <Route path="withdrawals" element={<WithdrawalQueue />} />
              <Route path="payments" element={<PaymentsAdmin />} />
              <Route path="tax" element={<TaxAdmin />} />
              <Route path="tickets" element={<TicketsAdmin />} />
              <Route path="cms" element={<Cms />} />
              <Route path="risk" element={<RiskPrivacy />} />
              <Route path="audit" element={<AuditLogs />} />
              <Route path="health" element={<Health />} />
              <Route path="admins" element={<Admins />} />
              <Route path="settings" element={<SystemSettings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

export default App;
